import { Briefcase, Fish, Home, Users, type LucideIcon } from "lucide-react";
import { Reveal } from "../Reveal";

type GuestProfile = {
  icon: LucideIcon;
  title: string;
  body: string;
  accent: string;
};

const PROFILES: GuestProfile[] = [
  {
    icon: Home,
    title: "RV Travelers and Snowbirds",
    body: "A peaceful year-round destination with full hookups and options for seasonal or extended stays.",
    accent: "01",
  },
  {
    icon: Fish,
    title: "Anglers and Boaters",
    body: "A convenient home base for guests visiting Zapata and Falcon Lake for fishing and boating.",
    accent: "02",
  },
  {
    icon: Briefcase,
    title: "Working and Corporate Guests",
    body: "Flexible RV sites and furnished accommodations for work assignments, crews, and longer stays in the region.",
    accent: "03",
  },
  {
    icon: Users,
    title: "Families and Groups",
    body: "Comfortable accommodations and gathering spaces for family reunions, celebrations, holidays, and group trips.",
    accent: "04",
  },
];

/**
 * Guest profiles as an editorial list (not a uniform card grid),
 * with numbered accents and subtle hover motion.
 */
export function WhoWeWelcome() {
  return (
    <section className="bg-surface py-24 sm:py-28 lg:py-32" aria-labelledby="welcome-guests-title">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
        <Reveal className="max-w-2xl">
          <p className="eyebrow text-clay">Who We Welcome</p>
          <h2
            id="welcome-guests-title"
            className="mt-4 font-display text-3xl leading-tight sm:text-4xl lg:text-[2.9rem]"
          >
            There Is a Place for You at Amigo Inn
          </h2>
        </Reveal>

        <ul className="mt-14 grid gap-x-12 gap-y-0 lg:grid-cols-2">
          {PROFILES.map((profile, i) => (
            <Reveal as="li" key={profile.title} delay={(i % 2) * 0.07} y={22}>
              <article className="group rule-sand flex gap-5 py-8 transition-transform duration-500 ease-out hover:-translate-y-0.5 lg:py-10">
                <div className="flex flex-col items-start gap-4">
                  <span
                    className="font-display text-sm tracking-[0.18em] text-sand"
                    aria-hidden="true"
                  >
                    {profile.accent}
                  </span>
                  <profile.icon
                    className="size-5 text-forest transition-colors duration-300 group-hover:text-clay"
                    aria-hidden="true"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-display text-xl text-forest-deep sm:text-2xl">
                    {profile.title}
                  </h3>
                  <p className="mt-3 max-w-md text-sm leading-relaxed text-muted-foreground sm:text-base">
                    {profile.body}
                  </p>
                </div>
              </article>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
}
